import {
  compareReviewQueue,
  isShownForWho,
  reviewersOfPerson,
  type RankingRow,
  type ReviewPerson,
  type ReviewStatus,
  type ReviewWho,
} from '@nest-recruit/shared';

// D6 (ADR 0080) — which Ranking rows the Review list shows, in which order, and how many sit behind each filter. Evening review
// 2026-11-01 item 8: the filters are All and one "Reviewed by" per reviewer, by name.

/** Everyone still in review: at `screening`, or still at `new` (the board's lanes take the same people). */
export function isInReview(row: Pick<RankingRow, 'stage'>): boolean {
  return row.stage === 'screening' || row.stage === 'new';
}

/** What waits on HR comes first: To review, then Maybe (§L31's queue), then those already shortlisted. */
const STATUS_ORDER: Readonly<Record<ReviewStatus, number>> = { to_review: 0, maybe: 1, shortlisted: 2 };

/** The list's order before anyone sorts a column: the review queue (§L31, by rank key), with HR's picks after it. */
export function compareReviewList(a: RankingRow, b: RankingRow): number {
  const byStatus = STATUS_ORDER[a.reviewStatus ?? 'to_review'] - STATUS_ORDER[b.reviewStatus ?? 'to_review'];
  if (byStatus !== 0) return byStatus;
  return compareReviewQueue(
    { id: a.applicationId, rankKey: a.rankKey, reviewStatus: a.reviewStatus, importOrder: a.order },
    { id: b.applicationId, rankKey: b.rankKey, reviewStatus: b.reviewStatus, importOrder: b.order },
  );
}

/** Whether a row's name or city holds the (already lower-cased) search, as on the board. */
export function matchesReviewSearch(row: Pick<RankingRow, 'name' | 'city'>, query: string): boolean {
  if (query === '') return true;
  return [row.name, row.city].some((value) => value !== null && value.toLowerCase().includes(query));
}

export interface ReviewerCount {
  readonly userId: string;
  readonly name: string;
  readonly count: number;
}

export interface ReviewListRows {
  /** Shown under the chosen filter and search, in the list's own order. */
  readonly rows: readonly RankingRow[];
  /** Everyone in review under the same search: the All count. */
  readonly total: number;
  /** Each reviewer under the same search, with how many they reviewed: most first, then by name. */
  readonly reviewers: readonly ReviewerCount[];
}

export function reviewListRowsOf(rows: readonly RankingRow[], people: ReadonlyMap<string, ReviewPerson>, who: ReviewWho, query: string): ReviewListRows {
  const searched = rows.filter((row) => isInReview(row) && matchesReviewSearch(row, query));
  const reviewers = new Map<string, { name: string; count: number }>();
  for (const row of searched) {
    for (const reviewer of reviewersOfPerson(row, people.get(row.applicationId))) {
      const seen = reviewers.get(reviewer.userId);
      reviewers.set(reviewer.userId, { name: seen?.name ?? reviewer.name ?? 'A former colleague', count: (seen?.count ?? 0) + 1 });
    }
  }
  const shown = searched.filter((row) => isShownForWho(who, row, people.get(row.applicationId))).sort(compareReviewList);
  return {
    rows: shown,
    total: searched.length,
    reviewers: [...reviewers.entries()].map(([userId, { name, count }]) => ({ userId, name, count })).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name)),
  };
}
